import React, { useMemo } from 'react';
import type { CalendarWeekView, CalendarSlot } from '../../../types';

// Booking status styling helper
const getBookingStyles = (status: string | undefined, isConflict: boolean = false) => {
  const isPending = status === 'pending';

  if (isConflict) {
    return {
      isPending: false,
      isConflict: true,
      mobile: {
        container: 'border-red-400 bg-red-100',
        indicator: 'w-1.5 h-1.5 rounded-full bg-red-500',
      },
      desktop: {
        container: 'border-red-400 bg-red-50 hover:bg-red-100',
        durationText: 'text-red-600',
      },
    };
  }

  return {
    isPending,
    isConflict: false,
    // Mobile styles
    mobile: {
      container: isPending
        ? 'border-purple-300 bg-gradient-to-br from-purple-50 via-white to-purple-50 border-dashed'
        : 'border-purple-400 bg-purple-100',
      indicator: isPending
        ? 'w-1.5 h-1.5 rounded-full bg-purple-400 animate-pulse'
        : 'w-1.5 h-1.5 rounded-full bg-purple-500',
    },
    // Desktop styles
    desktop: {
      container: isPending
        ? 'border-purple-300 border-dashed bg-[repeating-linear-gradient(135deg,#faf5ff,#faf5ff_4px,#f3e8ff_4px,#f3e8ff_8px)] hover:bg-[repeating-linear-gradient(135deg,#f3e8ff,#f3e8ff_4px,#ede9fe_4px,#ede9fe_8px)]'
        : 'border-purple-400 bg-purple-50 hover:bg-purple-100',
      durationText: isPending ? 'text-purple-500' : 'text-purple-600',
    },
  };
};

interface WeekViewProps {
  data: CalendarWeekView;
  onSlotClick?: (slot: CalendarSlot, date: string) => void;
  onBookingClick?: (bookingId: string) => void;
  onDayClick?: (date: string) => void;
  currency?: string;
  onNavigatePrevious?: () => void;
  onNavigateNext?: () => void;
  onGoToToday?: () => void;
}

interface ProcessedBooking {
  id: string;
  startSlotIndex: number;
  slotSpan: number;
  visualSpan: number; // Rounded up to nearest hour for display
  slot: CalendarSlot;
}

interface ProcessedBusySlot {
  id: string;
  startSlotIndex: number;
  slotSpan: number;
  visualSpan: number;
  slot: CalendarSlot;
}

interface DayProcessedSlots {
  bookings: ProcessedBooking[];
  slotBookingMap: Map<number, ProcessedBooking>;
  busySlots: ProcessedBusySlot[];
  slotBusyMap: Map<number, ProcessedBusySlot>;
}

// Calculate visual span from time_range, rounded up to nearest hour
const calculateVisualSpan = (timeRange: string | undefined, fallbackSpan: number): number => {
  if (!timeRange) return Math.max(1, fallbackSpan);

  // Parse time_range like "15:00-15:45" or "09:00-11:00"
  const match = timeRange.match(/(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})/);
  if (!match) return Math.max(1, fallbackSpan);

  const [, startHour, startMin, endHour, endMin] = match;
  const startMinutes = parseInt(startHour) * 60 + parseInt(startMin);
  const endMinutes = parseInt(endHour) * 60 + parseInt(endMin);
  const durationMinutes = endMinutes - startMinutes;

  // Round up to nearest hour (60 minutes)
  const visualHours = Math.ceil(durationMinutes / 60);
  return Math.max(1, visualHours);
};

export const WeekView: React.FC<WeekViewProps> = ({
  data,
  onSlotClick,
  onBookingClick,
  onDayClick,
  currency = 'USD',
  onNavigatePrevious,
  onNavigateNext,
  onGoToToday,
}) => {
  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100);
  };

  const isToday = (dateStr: string) => {
    const today = new Date().toISOString().split('T')[0];
    const slotDate = new Date(dateStr).toISOString().split('T')[0];
    return today === slotDate;
  };

  const handleSlotClick = (slot: CalendarSlot, date: string) => {
    if ((slot.status === 'booked' || slot.status === 'conflict') && slot.booking) {
      onBookingClick?.(slot.booking.id);
    } else if (slot.status === 'available') {
      onSlotClick?.(slot, date);
    }
  };

  // Mobile: Navigate to day view for available slots
  const handleMobileSlotClick = (date: string) => {
    onDayClick?.(date);
  };

  // Mobile: Open booking modal directly for bookings
  const handleMobileBookingClick = (bookingId: string) => {
    onBookingClick?.(bookingId);
  };

  // Get 2-letter weekday abbreviations
  const getWeekdayShort = (weekday: string) => {
    return weekday.substring(0, 2);
  };

  // Process slots for each day to identify multi-hour bookings and busy slots
  const processedDays = useMemo(() => {
    return data.days.map((day): DayProcessedSlots => {
      const bookings: ProcessedBooking[] = [];
      const bookingMap = new Map<number, ProcessedBooking>();
      const seenBookingIds = new Set<string>();

      const busySlots: ProcessedBusySlot[] = [];
      const busyMap = new Map<number, ProcessedBusySlot>();
      const seenBusyIds = new Set<string>();

      // Filter to only available, booked, busy, conflict, and no_service_fit slots for processing
      const visibleSlots = day.slots.filter(
        slot => slot.status === 'available' || slot.status === 'booked' || slot.status === 'busy' || slot.status === 'no_service_fit' || slot.status === 'conflict'
      );

      visibleSlots.forEach((slot, index) => {
        if ((slot.status === 'booked' || slot.status === 'conflict') && slot.booking) {
          if (!seenBookingIds.has(slot.booking.id)) {
            // First occurrence of this booking
            seenBookingIds.add(slot.booking.id);

            // Count how many consecutive slots this booking spans
            let span = 1;
            for (let i = index + 1; i < visibleSlots.length; i++) {
              const nextSlot = visibleSlots[i];
              if ((nextSlot.status === 'booked' || nextSlot.status === 'conflict') && nextSlot.booking?.id === slot.booking.id) {
                span++;
              } else {
                break;
              }
            }

            // Calculate visual span from time_range (rounded up to nearest hour)
            const visualSpan = calculateVisualSpan(slot.booking.time_range, span);

            const processedBooking: ProcessedBooking = {
              id: slot.booking.id,
              startSlotIndex: index,
              slotSpan: span,
              visualSpan: visualSpan,
              slot: slot,
            };

            bookings.push(processedBooking);
            bookingMap.set(index, processedBooking);
          }
        } else if (slot.status === 'busy' && slot.google_event) {
          const eventId = slot.google_event.id;
          if (!seenBusyIds.has(eventId)) {
            // First occurrence of this Google Calendar event
            seenBusyIds.add(eventId);

            // Count how many consecutive slots this event spans
            let span = 1;
            for (let i = index + 1; i < visibleSlots.length; i++) {
              const nextSlot = visibleSlots[i];
              if (nextSlot.status === 'busy' && nextSlot.google_event?.id === eventId) {
                span++;
              } else {
                break;
              }
            }

            // Calculate visual span from start_time and end_time
            const timeRange = `${slot.google_event.start_time}-${slot.google_event.end_time}`;
            const visualSpan = calculateVisualSpan(timeRange, span);

            const processedBusy: ProcessedBusySlot = {
              id: eventId,
              startSlotIndex: index,
              slotSpan: span,
              visualSpan: visualSpan,
              slot: slot,
            };

            busySlots.push(processedBusy);
            busyMap.set(index, processedBusy);
          }
        }
      });

      return { bookings, slotBookingMap: bookingMap, busySlots, slotBusyMap: busyMap };
    });
  }, [data.days]);

  // Constants for slot sizing
  const SLOT_HEIGHT = 44; // px - minimum touch target (mobile)
  const SLOT_HEIGHT_DESKTOP = 60; // px - desktop slot height
  const GAP_SIZE = 4; // px - gap-1 in Tailwind

  // Calculate height for multi-hour booking using visual span
  const calculateBookingHeight = (visualSpan: number, isDesktop = false): number => {
    const baseHeight = isDesktop ? SLOT_HEIGHT_DESKTOP : SLOT_HEIGHT;
    return baseHeight * visualSpan + GAP_SIZE * (visualSpan - 1);
  };

  // Calculate duration text from visual span
  const getDurationText = (visualSpan: number) => {
    if (visualSpan === 1) return '1 hour';
    return `${visualSpan} hours`;
  };

  return (
    <>
      {/* Mobile Layout - Compact 7 columns */}
      <div className="md:hidden">
        {/* Day headers - 7 columns */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {data.days.map((day, index) => (
            <div
              key={index}
              className={`text-center py-2 rounded-lg ${
                isToday(day.date) ? 'bg-purple-100' : ''
              }`}
            >
              <div
                className={`text-xs font-medium ${
                  isToday(day.date) ? 'text-purple-600' : 'text-gray-500'
                }`}
              >
                {getWeekdayShort(day.weekday)}
              </div>
              <div
                className={`text-sm font-bold mt-0.5 ${
                  isToday(day.date) ? 'text-purple-600' : 'text-gray-900'
                }`}
              >
                {day.day_number}
              </div>
            </div>
          ))}
        </div>

        {/* Slots grid - 7 columns */}
        <div className="grid grid-cols-7 gap-1">
          {data.days.map((day, dayIndex) => {
            const { slotBookingMap, slotBusyMap } = processedDays[dayIndex];

            // Filter to visible slots (available, booked, busy, or conflict)
            const visibleSlots = day.slots.filter(
              slot => slot.status === 'available' || slot.status === 'booked' || slot.status === 'busy' || slot.status === 'conflict'
            );

            const hasConflicts = day.is_day_off && visibleSlots.some(s => s.status === 'conflict');

            return (
              <div key={dayIndex} className="flex flex-col gap-1 relative">
                {day.is_working_day || hasConflicts ? (
                  <>
                    {visibleSlots.length === 0 ? (
                      <div className="text-xs text-gray-400 text-center py-2">-</div>
                    ) : (
                      visibleSlots.map((slot, slotIndex) => {
                        const booking = slotBookingMap.get(slotIndex);
                        const isBookingStart = booking !== undefined;
                        const isBookingContinuation = (slot.status === 'booked' || slot.status === 'conflict') && !isBookingStart;
                        const isConflict = slot.status === 'conflict';

                        const busySlot = slotBusyMap.get(slotIndex);
                        const isBusyStart = busySlot !== undefined;
                        const isBusyContinuation = slot.status === 'busy' && !isBusyStart;

                        // Render placeholder for continuation slots to maintain layout
                        if (isBookingContinuation || isBusyContinuation) {
                          return (
                            <div
                              key={slotIndex}
                              style={{ height: `${SLOT_HEIGHT}px` }}
                              aria-hidden="true"
                            />
                          );
                        }

                        return (
                          <div
                            key={slotIndex}
                            className="relative"
                            style={{ height: `${SLOT_HEIGHT}px` }}
                          >
                            {isBookingStart && booking && slot.booking ? (
                              // Multi-hour booking - spans multiple slot heights
                              (() => {
                                const styles = getBookingStyles(slot.booking.status, isConflict);
                                return (
                                  <div
                                    className={`absolute inset-x-0 flex flex-col items-center justify-center px-1.5 py-2 rounded-lg border-2 cursor-pointer ${isConflict ? 'active:bg-red-200' : 'active:bg-purple-200'} transition-colors shadow-sm ${styles.mobile.container}`}
                                    style={{
                                      height: `${calculateBookingHeight(booking.visualSpan)}px`,
                                      top: 0,
                                    }}
                                    onClick={() => handleMobileBookingClick(slot.booking!.id)}
                                  >
                                    {/* Show hour for single slot, time range for multi-hour */}
                                    <span className="text-xs font-semibold text-gray-900">
                                      {booking.visualSpan === 1
                                        ? slot.time.split(':')[0]
                                        : `${slot.time.split(':')[0]}-${booking.visualSpan}h`
                                      }
                                    </span>
                                    {/* Status indicator - pulsing for pending, solid for confirmed, red for conflict */}
                                    <div className={`mt-1 ${styles.mobile.indicator}`}></div>
                                  </div>
                                );
                              })()
                            ) : isBusyStart && busySlot && slot.google_event ? (
                              // Multi-hour busy slot (Google Calendar event) - spans multiple slot heights
                              <div
                                className="absolute inset-x-0 flex flex-col items-center justify-center p-1.5 rounded-lg border border-gray-300 bg-gray-100"
                                style={{
                                  height: `${calculateBookingHeight(busySlot.visualSpan)}px`,
                                  top: 0,
                                }}
                              >
                                <span className="text-xs font-medium text-gray-500">
                                  {busySlot.visualSpan === 1
                                    ? slot.time.split(':')[0]
                                    : `${slot.time.split(':')[0]}-${busySlot.visualSpan}h`
                                  }
                                </span>
                                <span className="text-[9px] text-gray-400 mt-0.5 truncate max-w-full px-1">
                                  {slot.google_event.summary || 'Busy'}
                                </span>
                              </div>
                            ) : slot.status === 'busy' ? (
                              // Single busy slot (fallback)
                              <div
                                className="absolute inset-0 flex flex-col items-center justify-center p-1.5 rounded-lg border border-gray-300 bg-gray-100"
                              >
                                <span className="text-xs font-medium text-gray-500">
                                  {slot.time.split(':')[0]}
                                </span>
                                <span className="text-[9px] text-gray-400 mt-0.5">Busy</span>
                              </div>
                            ) : slot.status === 'available' ? (
                              // Available slot - standard size
                              <div
                                className="absolute inset-0 flex flex-col items-center justify-center p-1.5 rounded-lg border border-gray-200 bg-white cursor-pointer active:bg-gray-50 transition-colors"
                                onClick={() => handleMobileSlotClick(day.date)}
                              >
                                <span className="text-xs font-medium text-gray-900">
                                  {slot.time.split(':')[0]}
                                </span>
                              </div>
                            ) : slot.status === 'no_service_fit' ? (
                              // No service fits - disabled slot
                              <div
                                className="absolute inset-0 flex flex-col items-center justify-center p-1.5 rounded-lg border border-gray-200 bg-gray-50 opacity-50"
                              >
                                <span className="text-xs font-medium text-gray-400">
                                  {slot.time.split(':')[0]}
                                </span>
                              </div>
                            ) : null}
                          </div>
                        );
                      })
                    )}
                  </>
                ) : (
                  <div className="text-xs text-gray-400 text-center py-2">Off</div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="hidden md:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {onNavigatePrevious && (
                <button
                  onClick={onNavigatePrevious}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-md transition-colors"
                  aria-label="Previous week"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}
              <h3 className="text-lg font-semibold text-gray-900">
                {new Date(data.date_range.start).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                })}{' '}
                -{' '}
                {new Date(data.date_range.end).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </h3>
              {onNavigateNext && (
                <button
                  onClick={onNavigateNext}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-md transition-colors"
                  aria-label="Next week"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}
              {onGoToToday && (
                <button
                  onClick={onGoToToday}
                  className="ml-1 px-3 py-1 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-md transition-colors"
                >
                  Today
                </button>
              )}
            </div>
            <div className="flex gap-4 text-sm">
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">{data.summary.total_bookings}</div>
                <div className="text-gray-500">Bookings</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {formatCurrency(data.summary.total_revenue_cents)}
                </div>
                <div className="text-gray-500">Revenue</div>
              </div>
            </div>
          </div>
        </div>

        {/* Week grid */}
        <div className="overflow-x-auto">
          <div className="grid grid-cols-7 min-w-[1200px]">
            {/* Day headers */}
            {data.days.map((day, index) => {
              const monthShort = new Date(day.date).toLocaleDateString('en-US', { month: 'short' });
              return (
                <div
                  key={index}
                  className={`p-4 border-r border-b border-gray-200 text-center ${
                    isToday(day.date) ? 'bg-purple-100' : 'bg-gray-50'
                  }`}
                >
                  <div
                    className={`text-xs font-medium ${
                      isToday(day.date) ? 'text-purple-600' : 'text-gray-500'
                    }`}
                  >
                    {day.weekday}
                  </div>
                  <div
                    className={`text-2xl font-bold mt-1 ${
                      isToday(day.date) ? 'text-purple-600' : 'text-gray-900'
                    }`}
                  >
                    {day.day_number}
                  </div>
                  <div
                    className={`text-xs mt-0.5 ${
                      isToday(day.date) ? 'text-purple-500' : 'text-gray-400'
                    }`}
                  >
                    {monthShort}
                  </div>
                  {!day.is_working_day && (
                    <div className={`text-xs mt-1 ${day.is_day_off && day.slots.some(s => s.status === 'conflict') ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
                      {day.is_day_off && day.slots.some(s => s.status === 'conflict') ? 'Day off ⚠️' : 'Not working'}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Day columns with slots - using spanning logic */}
            {data.days.map((day, dayIndex) => {
              const { slotBookingMap, slotBusyMap } = processedDays[dayIndex];

              // Filter to visible slots (available, booked, busy, or conflict)
              const visibleSlots = day.slots.filter(
                slot => slot.status === 'available' || slot.status === 'booked' || slot.status === 'busy' || slot.status === 'conflict'
              );

              const hasConflictsDesktop = day.is_day_off && visibleSlots.some(s => s.status === 'conflict');

              return (
                <div key={dayIndex} className="border-r border-gray-200 min-h-[400px]">
                  {day.is_working_day || hasConflictsDesktop ? (
                    <div className="p-2 flex flex-col gap-1">
                      {visibleSlots.map((slot, slotIndex) => {
                        const booking = slotBookingMap.get(slotIndex);
                        const isBookingStart = booking !== undefined;
                        const isBookingContinuation = (slot.status === 'booked' || slot.status === 'conflict') && !isBookingStart;
                        const isConflict = slot.status === 'conflict';

                        const busySlot = slotBusyMap.get(slotIndex);
                        const isBusyStart = busySlot !== undefined;
                        const isBusyContinuation = slot.status === 'busy' && !isBusyStart;

                        // Render placeholder for continuation slots
                        if (isBookingContinuation || isBusyContinuation) {
                          return (
                            <div
                              key={slotIndex}
                              style={{ height: `${SLOT_HEIGHT_DESKTOP}px` }}
                              aria-hidden="true"
                            />
                          );
                        }

                        return (
                          <div
                            key={slotIndex}
                            className="relative"
                            style={{ height: `${SLOT_HEIGHT_DESKTOP}px` }}
                          >
                            {isBookingStart && booking && slot.booking ? (
                              // Booking card - row-based layout matching mobile style
                              (() => {
                                const styles = getBookingStyles(slot.booking.status, isConflict);
                                return (
                                  <div
                                    className={`absolute inset-x-0 top-0 px-2 rounded-lg border-2 cursor-pointer ${isConflict ? 'hover:border-red-500' : 'hover:border-purple-500'} hover:shadow-md transition-all z-10 flex items-center ${styles.desktop.container}`}
                                    style={{
                                      height: `${calculateBookingHeight(booking.visualSpan, true)}px`,
                                    }}
                                    onClick={() => handleSlotClick(slot, day.date)}
                                  >
                                    {/* Left: Time + Duration */}
                                    <div className="w-16 flex-shrink-0">
                                      <div className="text-[11px] font-semibold text-gray-900 leading-tight">
                                        {slot.booking.time_range}
                                      </div>
                                      <div className={`text-[10px] font-medium leading-tight ${styles.desktop.durationText}`}>
                                        {isConflict ? '⚠️ ' : styles.isPending ? '⏳ ' : ''}{getDurationText(booking.visualSpan)}
                                      </div>
                                    </div>

                                    {/* Middle: Client | Service */}
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-1">
                                        <span className="text-[11px] font-medium text-gray-900 truncate">
                                          {slot.booking.client.name}
                                        </span>
                                        <span className="text-gray-300 flex-shrink-0">|</span>
                                        <span className="text-[11px] text-gray-600 truncate">
                                          {slot.booking.service.name}
                                        </span>
                                      </div>
                                    </div>

                                    {/* Right: Arrow */}
                                    <div className="flex-shrink-0 ml-1">
                                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                      </svg>
                                    </div>
                                  </div>
                                );
                              })()
                            ) : isBusyStart && busySlot && slot.google_event ? (
                              // Multi-hour busy slot (Google Calendar event) - spans multiple slot heights
                              <div
                                className="absolute inset-x-0 top-0 flex items-center px-2 rounded-lg border border-gray-300 bg-gray-100 z-10"
                                style={{
                                  height: `${calculateBookingHeight(busySlot.visualSpan, true)}px`,
                                }}
                              >
                                {/* Left: Time range + Duration */}
                                <div className="w-16 flex-shrink-0">
                                  <div className="text-[11px] font-semibold text-gray-500 leading-tight">
                                    {slot.google_event.start_time}-{slot.google_event.end_time}
                                  </div>
                                  <div className="text-[10px] font-medium text-gray-400 leading-tight">
                                    {getDurationText(busySlot.visualSpan)}
                                  </div>
                                </div>

                                {/* Middle: Event name */}
                                <div className="flex-1 min-w-0">
                                  <span className="text-[11px] text-gray-500 font-medium truncate">
                                    {slot.google_event.summary || 'Busy'}
                                  </span>
                                </div>
                              </div>
                            ) : slot.status === 'busy' ? (
                              // Single busy slot (fallback)
                              <div
                                className="absolute inset-0 flex items-center px-2 rounded-lg border border-gray-300 bg-gray-100"
                              >
                                <div className="w-12 flex-shrink-0">
                                  <span className="text-xs font-semibold text-gray-500">{slot.time}</span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <span className="text-[11px] text-gray-500 font-medium">Busy</span>
                                </div>
                              </div>
                            ) : slot.status === 'available' ? (
                              // Available slot - row layout matching mobile
                              <div
                                className={`absolute inset-0 flex items-center px-2 rounded-lg border cursor-pointer transition-colors ${
                                  isToday(day.date)
                                    ? 'bg-purple-50/30 border-gray-200 hover:bg-green-50 hover:border-green-300'
                                    : 'bg-white border-gray-200 hover:bg-green-50 hover:border-green-300'
                                }`}
                                onClick={() => handleSlotClick(slot, day.date)}
                              >
                                {/* Left: Time */}
                                <div className="w-12 flex-shrink-0">
                                  <span className="text-xs font-semibold text-gray-900">{slot.time}</span>
                                </div>

                                {/* Middle: Available label */}
                                <div className="flex-1">
                                  <span className="text-[11px] text-green-600 font-medium">Available</span>
                                </div>

                                {/* Right: Arrow */}
                                <div className="flex-shrink-0">
                                  <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                </div>
                              </div>
                            ) : slot.status === 'no_service_fit' ? (
                              // No service fits - disabled slot
                              <div
                                className="absolute inset-0 flex items-center px-2 rounded-lg bg-gray-50 border border-gray-200 opacity-60"
                                title="No services fit this time slot"
                              >
                                <div className="w-12 flex-shrink-0">
                                  <span className="text-xs font-semibold text-gray-400">{slot.time}</span>
                                </div>
                                <div className="flex-1">
                                  <span className="text-[11px] text-gray-400">No services fit</span>
                                </div>
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="h-full flex items-center justify-center text-gray-400 text-sm">
                      Day off
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};
